<template>
  <div class="profile__wrapper">
    <profile-top
      :photo="photo"
      :name="name"
      :position="position"
      :department="department"
      :company="company"
      :activityTag="activityTag"
    >
      <template v-slot:state>
        <profile-status :status="participant.status" />
      </template>
    </profile-top>
    <popup-card
      v-if="isExchangePopup"
      :is-open="isExchangePopup"
      @show="isExchangePopup = false"
    >
      <div class="exchange-popup">
        <h4 class="exchange-popup__title">
          {{ $t(`participantProfile.exchange.${isIncoming ? 'incoming' : 'outgoing'}_title`) }}
        </h4>
        <p class="exchange-popup__text">
          {{ $t(`participantProfile.exchange.${isIncoming ? 'incoming' : 'outgoing'}_text`) }}
        </p>
        <div class="exchange-popup__actions">
          <template v-if="isIncoming">
            <button
              type="button"
              class="exchange-popup__btn is-primary"
              @click="acceptExchange"
            >
              {{ $t('participantProfile.exchange.accept') }}
            </button>
            <button
              type="button"
              class="exchange-popup__btn"
              @click="declineExchange"
            >
              {{ $t('participantProfile.exchange.decline') }}
            </button>
          </template>
          <!--
            Отмена стоит второй и намеренно тихая. Когда кнопка в попапе одна,
            её читают как главное действие — и «Отозвать запрос» принимали за
            «отправить ещё раз», тем более что слово похоже на «отослать».
            Человек сюда чаще всего заходит просто посмотреть, чем кончилось,
            поэтому по умолчанию ему предлагается закрыть.
          -->
          <template v-else>
            <button
              type="button"
              class="exchange-popup__btn is-primary"
              @click="isExchangePopup = false"
            >
              {{ $t('participantProfile.exchange.close') }}
            </button>
            <button
              type="button"
              class="exchange-popup__btn is-quiet"
              @click="revokeExchange"
            >
              {{ $t('participantProfile.exchange.revoke') }}
            </button>
          </template>
        </div>
      </div>
    </popup-card>
    <div class="profile-system__wrapper">
      <div class="profile-content full-size">
        <div
          v-if="isHotLink"
          class="hot-link-panel"
        >
            <div class="hot-link-row">
              <button
                v-if="canMark"
                @click="toggleFavorite"
                :aria-label="$t(marked ? 'participantList.unmark' : 'participantList.mark')"
                class="marked-profile"
              >
                <i :class="[marked ? 'exu-bookmark-check-fill' : 'exu-bookmark']"/>
              </button>
              <div class="hot-link-group">
                <up-card
                  v-if="isMessenger"
                  :class="{ 'hot-link--locked': !isMutualFavorite }"
                  @click.stop="getChatUuid"
                >
                  <div class="hot-link">
                    <i class="exu-message-chat-square" />
                  </div>
                </up-card>
                <up-card
                  v-if="isMM"
                  :class="{ 'hot-link--locked': !isMutualFavorite }"
                >
                  <router-link
                    v-if="isMutualFavorite && isMeetUuid"
                    :to="{ name: 'editMatch', params: { matchId: this.participant.meet_uuid } }"
                  >
                    <i class="exu-user-meeting"/>
                  </router-link>
                  <router-link
                    v-else-if="isMutualFavorite"
                    :to="{ name: 'newMatch', params: { participantID: this.id } }"
                  >
                    <i class="exu-user-meeting"/>
                  </router-link>
                  <div
                    v-else
                    class="hot-link"
                  >
                    <i class="exu-user-meeting"/>
                  </div>
                </up-card>
                <up-card
                  v-if="isBusinessCard"
                  :class="{ 'hot-link--locked': !isMutualFavorite }"
                  :aria-label="exchangeLabel"
                  @click.stop="openExchange"
                >
                  <div class="hot-link">
                    <span class="exchange-icon">
                      <i class="exu-persone-card"/>
                      <i
                        v-if="exchangeOverlay"
                        :class="[
                          exchangeOverlay.icon,
                          'exchange-icon__mark',
                          { 'is-accent': exchangeOverlay.accent },
                        ]"
                      />
                    </span>
                  </div>
                </up-card>
              </div>
            </div>
            <p
              v-if="mutualHintKey"
              class="hot-link-hint"
            >
              {{ $t(mutualHintKey) }}
            </p>
        </div>
      </div>
    </div>
    <div v-if="visitStatus" class="profile-system__wrapper">
      <div class="profile-content full-size">
        <div
          :class="visitStatus.cssClass"
          class="visit-status"
        >
          <i class="exu-clock"></i>
          <span>{{ visitStatus.text }}</span>
        </div>
      </div>
    </div>
    <profile-rubrics :rubrics="participant.rubrics" />
    <div class="profile-content__wrapper">
      <div
        v-if="description"
        class="profile-content"
      >
        <p class="description-paragraph">
          {{ description }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import appBaseNav from '@/components/mixins/appBaseNav';
import participants from '@/components/mixins/methodsParticipants';
import methodsChat from '@/components/mixins/methodsChat';
import methodsMeetings from '@/components/mixins/api/apiMethodsMeeting';
import favorite, { FAVORITE_API_READY } from '@/components/mixins/api/apiMethodsParticipantFavorite';
import businessCard from '@/components/mixins/methodsBusinessCard';
import demoCatalog from '@/components/mixins/demo/demoParticipantCatalog';
import ProfileRubrics from '@/components/shared/ProfileRubrics.vue';
import ProfileStatus from '@/components/shared/ProfileStatus.vue';

export default {
  name: 'ParticipantProfile',
  components: { ProfileRubrics, ProfileStatus },
  props: {
    id: {
      requared: true,
    },
    locale: {
      type: String,
    },
  },
  data() {
    return {
      participant: {
        type: Object,
        default: () => ({}),
      },
      // Состояние обмена визитками с этим участником. Приходит одним ответом
      // /business-card/status/{userId}: сам обмен и, у двух ожидающих
      // состояний, номер запроса — чтобы принять или отозвать прямо отсюда.
      exchange: {
        status: null,
        requestId: null,
      },
      // Есть ли своя визитка. null — ещё не спрашивали.
      isCardCreated: null,
      ownCardRequest: null,
      isExchangePopup: false,
    };
  },
  mixins: [
    appBaseNav,
    participants,
    methodsChat,
    methodsMeetings,
    favorite,
    businessCard,
    demoCatalog,
  ],
  computed: {
    name() {
      const lname = this.participant.lname && this.participant.lname.trim();
      const fname = this.participant.fname && this.participant.fname.trim();
      const sname = this.participant.sname && this.participant.sname.trim();
      return `${lname} ${fname}${sname ? ` ${sname}` : ''}`;
    },
    position() {
      return this.participant.position || '';
    },
    department() {
      return this.participant.department || '';
    },
    company() {
      return this.participant.company || '';
    },
    photo() {
      return this.participant.photo || '';
    },
    activityTag() {
      return this.participant.activity || '';
    },
    description() {
      return this.participant.description || '';
    },
    isMessenger() {
      return this.$store.getters.isMessenger;
    },
    isMM() {
      return this.$store.getters.isMM;
    },
    isMeetUuid() {
      return this.isMM && this.participant.meet_uuid;
    },
    // Состояние закладки живёт в самой карточке: от него же зависит доступность
    // чата и встречи, и два источника разъехались бы после первого нажатия.
    marked() {
      return this.participant.is_favorite === true;
    },
    // Общение открыто только при взаимной отметке: я добавил участника, и он
    // добавил меня. Пока признак не пришёл (гость, недогруженная карточка) —
    // считаем, что взаимности нет.
    isMutualFavorite() {
      return this.participant.is_favorite === true && this.participant.favorited_me === true;
    },
    // Подсказка нужна, только когда есть что объяснять: хотя бы одна из плиток
    // на экране и она погашена. Текст зависит от того, чьего шага не хватает —
    // «ждите» вместо «добавьте» сбивает с толку того, кого уже отметили.
    mutualHintKey() {
      if (this.isMutualFavorite) return null;
      if (!(this.isMessenger || this.isMM || this.isBusinessCard)) return null;
      if (this.participant.is_favorite === true) return 'participantProfile.mutual_hint_waiting';
      if (this.participant.favorited_me === true) return 'participantProfile.mutual_hint_answer';
      return 'participantProfile.mutual_hint_mark';
    },
    // закладка есть только у авторизованного: избранное хранится за учётной записью
    canMark() {
      return !!this.$store.getters.getToken;
    },
    isBusinessCard() {
      return this.$store.getters.isBusinessCard;
    },
    // Сервер называет исходное состояние null, контракт — 'none'. Приводим к
    // одному виду: всё неизвестное считаем «обмена нет», иначе плитка не
    // нарисуется ни в одном из четырёх состояний.
    exchangeState() {
      const KNOWN = ['pending_outgoing', 'pending_incoming', 'exchanged'];
      return KNOWN.includes(this.exchange.status) ? this.exchange.status : 'none';
    },
    isIncoming() {
      return this.exchangeState === 'pending_incoming';
    },
    // Основа плитки одна во всех состояниях — визитка; состояние читается по
    // накладке в углу. Часы значат «ждёшь ты», колокольчик — «ждут тебя», и
    // только он акцентный: это единственное состояние, где нужен ответ.
    exchangeOverlay() {
      switch (this.exchangeState) {
        case 'pending_outgoing':
          return { icon: 'exu-clock' };
        case 'pending_incoming':
          return { icon: 'exu-bell-ringing-3', accent: true };
        case 'exchanged':
          return { icon: 'exu-check' };
        default:
          return null;
      }
    },
    exchangeLabel() {
      return this.$t(`participantProfile.exchange.aria_${this.exchangeState}`);
    },
    isHotLink() {
      return this.isMessenger || this.isMM || this.canMark || this.isBusinessCard;
    },
    visitStatus() {
      const TYPE_STATUS = ['never', 'several', 'today'];
      const lastVisit = this.participant.last_visit;
      return lastVisit && TYPE_STATUS.includes(lastVisit) ? {
        cssClass: lastVisit,
        text: this.$t(`participantProfile.visit_status.${lastVisit}`),
      } : null;
    },
  },
  watch: {
    locale() {
      this.$store.commit('setPageTitle', this.$t('participantProfile.header_title'));
    },
    // Пока отметка не взаимна, плитка визитки погашена и спрашивать нечего:
    // просмотр такого профиля не стоит ни одного лишнего запроса. Взаимность
    // приходит вместе с карточкой участника, а может появиться и прямо здесь —
    // человек нажал закладку, и она стала взаимной.
    isMutualFavorite(value) {
      if (value) this.syncExchange();
    },
  },
  mounted() {
    this.$store.commit('setPageTitle', this.$t('participantProfile.header_title'));
    this.getParticipantPersone(this.id)
      .then((result) => {
        // до готовности бэка поля избранного дописывает эмулятор каталога
        const card = FAVORITE_API_READY ? result : this.demoApplyFavoriteFields([result])[0];
        this.participant = { ...card };
        return this.getMeetingByUser(this.id);
      })
      .then((result) => {
        const MEET = Array.isArray(result) && result[0] ? result[0] : null;
        if (MEET && MEET.uuid) {
          this.participant.meet_uuid = MEET.uuid;
        }
      });
  },
  methods: {
    toggleFavorite() {
      const wanted = !this.marked;
      // отметку показываем сразу, вместе с ней меняется доступность общения
      this.participant.is_favorite = wanted;
      this.setFavorite(Number(this.id), wanted)
        .catch((error) => {
          this.participant.is_favorite = !wanted;
          // eslint-disable-next-line no-console
          console.error('toggleFavorite()::', error);
        });
    },
    // Два вопроса, два запроса: в каких мы отношениях и есть ли своя визитка.
    // Второй спрашиваем один раз за экран — визитка за время просмотра чужого
    // профиля не появится и не исчезнет.
    syncExchange() {
      if (!this.isBusinessCard) return;
      this.getBusinessCardStatus(this.id)
        .then((result) => {
          this.exchange.status = result.exchange_status || null;
          this.exchange.requestId = result.request_id || null;
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('syncExchange()::', error);
        });
      this.ensureOwnCard();
    },
    toast(key, type) {
      this.$store.commit('pushToast', {
        text: this.$t(`participantProfile.exchange.${key}`),
        type,
      });
    },
    // При обрыве связи о причине уже сказал baseApi, и своё «не получилось»
    // рядом с ним — второе сообщение об одном и том же.
    toastFailure(error) {
      if (error && error.code === 'ERR_NETWORK') return;
      this.toast('toast_failed', 'danger');
    },
    // Признак своей визитки спрашиваем один раз за экран и держим обещание
    // запроса: нажать могут раньше, чем он вернётся, а до ответа считать, что
    // визитка есть, нельзя — иначе человек без визитки примет обмен. Проверка
    // строго на true: и null, и отсутствие поля, и сбой запроса значат «нет».
    ensureOwnCard() {
      if (this.isCardCreated !== null) return Promise.resolve(this.isCardCreated === true);
      if (!this.ownCardRequest) {
        this.ownCardRequest = this.getMyBusinessCard()
          .then((data) => {
            this.isCardCreated = data.is_created === true;
            return this.isCardCreated;
          })
          .catch(() => {
            this.isCardCreated = false;
            return false;
          });
      }
      return this.ownCardRequest;
    },
    openExchange() {
      // клик по погашенной плитке не должен ничего делать, даже если стиль
      // почему-то не применился
      if (!this.isMutualFavorite) return;
      this.ensureOwnCard().then((hasOwnCard) => {
        // Обмен двусторонний: без своей визитки обмениваться нечем. Ведём её
        // создавать — в том числе когда обмен предложили нам, поэтому выбор
        // «принять или отклонить» в этом случае даже не показываем.
        if (!hasOwnCard) {
          this.toast('toast_need_card');
          this.$router.push({ name: 'businessCardEdit' });
          return;
        }
        if (this.exchangeState === 'exchanged') {
          this.$router.push({ name: 'businessCardCollectionItem', params: { userId: this.id } });
          return;
        }
        // Предложение отправляем сразу: оно обратимо тем же нажатием. А отзыв и
        // ответ на чужой запрос спрашиваем — промах по плитке иначе молча
        // отменит запрос или примет чужой.
        if (this.exchangeState === 'none') {
          this.offerExchange();
          return;
        }
        this.isExchangePopup = true;
      });
    },
    offerExchange() {
      this.sendBusinessCardRequest(Number(this.id))
        .then((result) => {
          this.exchange.status = 'pending_outgoing';
          this.exchange.requestId = result ? result.request_id : null;
        })
        .catch((error) => {
          // Молча гасить нечего: иконка не изменится, и человек останется без
          // объяснения, почему нажатие ничего не дало.
          this.toastFailure(error);
          // eslint-disable-next-line no-console
          console.error('offerExchange()::', error);
        });
    },
    revokeExchange() {
      this.isExchangePopup = false;
      this.revokeBusinessCardRequest(this.exchange.requestId)
        .then(() => {
          this.exchange.status = null;
          this.exchange.requestId = null;
        })
        .catch((error) => {
          this.toastFailure(error);
          // eslint-disable-next-line no-console
          console.error('revokeExchange()::', error);
        });
    },
    acceptExchange() {
      this.isExchangePopup = false;
      this.acceptBusinessCardRequest(this.exchange.requestId)
        .then(() => {
          this.exchange.status = 'exchanged';
          this.exchange.requestId = null;
          this.toast('toast_exchanged');
        })
        .catch((error) => {
          this.toastFailure(error);
          // eslint-disable-next-line no-console
          console.error('acceptExchange()::', error);
        });
    },
    declineExchange() {
      this.isExchangePopup = false;
      this.declineBusinessCardRequest(this.exchange.requestId)
        .then(() => {
          this.exchange.status = null;
          this.exchange.requestId = null;
        })
        .catch((error) => {
          this.toastFailure(error);
          // eslint-disable-next-line no-console
          console.error('declineExchange()::', error);
        });
    },
    getChatUuid() {
      // клик по погашенной плитке не должен открывать чат, даже если стиль
      // почему-то не применился
      if (!this.isMutualFavorite) return;
      this.getChatRoomByUser(this.id)
        .then((results) => {
          this.$router.push({ name: 'chatView', params: { uuid: results.uuid } });
        })
        .catch((error) => {
          console.error(error);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

// Подсказка появляется и исчезает вместе с текстом: место под неё не
// резервируется. Здесь стоял min-height в две строки — защита от прыжка, когда
// шапка забирает себе всё, что убывает снизу. Защита оказалась лишней: колонка
// работает на сжатие (у .profile-content__wrapper flex-basis 100%), а шапка с
// flex-shrink: 0 в перераспределении не участвует и на месте стоит.
// Отступ снизу даёт row-gap самой колонки, свой не нужен.
.hot-link-hint {
  margin: 8px 0 0;
  color: $color-disabled;
  font-size: 13px;
  line-height: 18px;
  text-align: center;
}

.marked-profile {
  display: flex;
  flex: 0 0 auto;
  align-self: stretch;
  width: 3em;
  justify-content: center;
  align-items: center;
  color: $color-accent;
  font-size: 20px;
  line-height: 20px;
  border: 2px solid $color-accent;
  // 16px, как у блока справа: 25px на кнопке 60×58 давали форму ни круглую,
  // ни прямоугольную, и она спорила с соседями. Обособленность закладки держит
  // не форма, а промежуток.
  border-radius: 16px;
  background-color: transparent;

  position: relative;
  z-index: 100;
}
// .profile-content .hot-link-panel .upCard
.profile {
  &__wrapper {
    display: flex;
    flex-direction: column;
    row-gap: 24px;
    min-height: 100%;
    padding-bottom: 24px;
  }
  &-content {
  display: flex;
  padding: 0px 12px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
    &__wrapper {
      display: flex;
      width: 100%;
      max-width: 760px;
      margin: 0 auto;
      align-items: center;
      flex: 3 1 100%;
      align-self: stretch;
    }
    &.full-size {
      width: 100%;
    }
    .hot-link-panel {
      width: 100%;

      // Закладка обособлена, три обращения к участнику собраны в один блок:
      // это разные по смыслу действия, и закладка вдобавок акцентная — внутри
      // общего блока она читалась бы как выбранный сегмент переключателя.
      .hot-link-row {
        display: flex;
        align-items: stretch;
        // Высота задана ряду, а не кнопкам: по содержимому они садятся до 42px
        // и закладка выходит плоской, 60×42. При 60px она ровно квадратная —
        // её ширина те же 3em при кегле 20px, — а плитки получают воздух вокруг
        // иконки. Остальные тянутся за рядом сами, они stretch.
        min-height: 60px;
        // Промежуток шире внутреннего: он и отделяет закладку от блока,
        // иначе четыре кнопки читаются одним рядом без деления.
        gap: 16px;
      }

      // Приём из деловой программы, .button-line в SectionCard: скругляются
      // только внешние края ряда, внутренние остаются прямыми, и три кнопки
      // читаются одной деталью с отделениями. Условие по first/last-child, а
      // не по порядку в разметке, — тогда при вырезании модуля у клиента ряд
      // сойдётся сам, без правки стилей.
      .hot-link-group {
        display: flex;
        flex: 1 1 auto;
        gap: 8px;

        & > .upCard {
          flex: 1 1 0;
          min-width: 0;
          border-radius: 0;
          // Тень у upCard рассчитана на отдельно лежащую карточку. Внутри
          // одного блока каждая плитка отбрасывала бы свою, обводя себя по
          // кругу и споря с общим контуром ряда. Кнопки .button-line в
          // деловой программе тени не носят вовсе — здесь так же.
          box-shadow: none;

          &:first-child {
            border-top-left-radius: 16px;
            border-bottom-left-radius: 16px;
          }
          &:last-child {
            border-top-right-radius: 16px;
            border-bottom-right-radius: 16px;
          }
        }
      }

      .upCard {
        border: 2px solid $color-accent;
        border-radius: 16px;
        padding: 8px;

        & .inner-text, & .hot-link, & a {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: .5rem;
          text-decoration: none;
          color: $color-accent-strong;

          // Размеры подобраны на глаз, а не по одной линейке: глифы занимают в
          // кегле разную площадь, и одинаковый font-size даёт разный вес.
          // Визитка идёт в размере чата — у них одинаковая обводка в шрифте,
          // 29px на кегль 320px, значит и на экране она одинаковая. В 2.4rem
          // тот же штрих давал 3.5px против 2px у чата, отчего визитка и
          // выглядела жирной.
          & i {
            font-size: 1.4rem;

            // Исключение на один глиф. Обводка у него тоньше остальных,
            // 23px на кегль 320px против 29px, и в общем кегле он выглядит
            // легче соседей. В 1.75rem его штрих даёт те же 2px, что у них и
            // у рамки кнопки.
            &.exu-user-meeting {
              font-size: 1.75rem;
            }
          }
        }
        // Ссылка тоже должна занимать плитку целиком: у плитки встречи внутри
        // <a>, и без высоты она сжималась по содержимому, отчего иконка сидела
        // выше центра, чем у соседей.
        & .hot-link, & a {
          height: 100%;
          justify-content: center;
        }

        // Состояние обмена показывает маленькая накладка в правом нижнем углу
        // визитки. Основа во всех четырёх состояниях одна, поэтому человек
        // сначала узнаёт кнопку и только потом читает состояние. Белое кольцо
        // под накладкой отделяет её от глифа, иначе две иконки сливаются.
        //
        // Селектор нарочно длиннее, чем нужно для попадания: у правила размера
        // иконок выше ровно та же специфичность, и без лишнего класса накладка
        // получала бы 1.4rem по порядку строк, вровень с самой визиткой.
        & .exchange-icon {
          position: relative;
          display: inline-flex;
        }
        & .hot-link i.exchange-icon__mark {
          position: absolute;
          right: -7px;
          bottom: -5px;
          padding: 2px;
          // 12px — компромисс: в 10px часы читаются кружком, в 14px накладка
          // выгрызает угол рамки и спорит с самой визиткой
          font-size: 12px;
          line-height: 1;
          border-radius: 50%;
          background: #fefefe;

          // Акцентная только у входящего запроса: это единственное состояние,
          // где от человека ждут ответа, и оно подсвечивает себя само.
          &.is-accent {
            color: $color-accent;
          }
        }

        // Плитка недоступна, пока отметка не взаимна. Размеры и разметка те же,
        // что у рабочей, — меняется только цвет, иначе ряд выглядит сломанным.
        &.hot-link--locked {
          border-color: $color-disabled;
          pointer-events: none;

          & .inner-text, & .hot-link, & a {
            color: $color-disabled;
          }
          // погашенная плитка гасится целиком, акцент накладки тут не исключение
          & .hot-link i.exchange-icon__mark.is-accent {
            color: $color-disabled;
          }
        }
      }
    }
    .description-paragraph {
      color: $color-text-secondary;
      // font-family: Roboto;
      // font-size: 14px;
      text-align: justify;
      margin: 0;
    }
  }
  &-system {
    &__wrapper {
      display: flex;
      width: 100%;
      max-width: 760px;
      margin: 0 auto;
      align-items: center;
      flex: 0 1 auto;
      align-self: stretch;
    }
  }
}
.btn-messenger {
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: center;
  height: 60px;
  padding-left: 30px;
  padding-right: 30px;
  font-size: 1rem;
  color: $color-accent;
  border: 2px solid $color-accent;
  border-radius: 16px;
  background: $color-base;
  gap: .5rem;

  & .inner-text {
    display: flex;
    align-items: baseline;
    gap: .5rem;
  }
}
.exchange-popup {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__title {
    margin: 0;
    color: $color-text;
  }
  &__text {
    margin: 0;
    // #707176 вместо палитрового $color-disabled: тот даёт 2.9 : 1 на белом и
    // норму контраста не проходит, а это единственное объяснение в попапе
    color: #707176;
    font-size: 14px;
    line-height: 20px;
  }
  &__actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  &__btn {
    padding: 10px 16px;
    color: $color-primary;
    font-size: 1rem;
    border: 2px solid $color-primary;
    border-radius: 16px;
    background: $color-base;

    &.is-primary {
      color: $color-base;
      background: $color-accent;
    }

    // Действие, которое человек выбирает редко и осознанно: без рамки и
    // приглушённое, чтобы не соперничать с кнопкой закрытия. #707176 даёт
    // 4.83 : 1 на белом, палитровый $color-disabled — только 2.9 : 1.
    &.is-quiet {
      color: #707176;
      border-color: transparent;
    }
  }
}
.visit-status {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 auto;
  border-radius: 14px;
  padding: 0 14px 0 6px;

  &.never {
    border: 2px solid $color-disabled;
    color: $color-disabled;
  }
  &.several {
    border: 2px solid #F0D040;
    color: #C0A020;
  }
  &.today {
    border: 2px solid #40F040;
    color: #15aa10;
  }
}
</style>
